import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { dateOnly, todayISO } from '../../lib/format';
import { useTRPC } from '../../lib/trpc';
import { PageRoot, PageHeader, KpiGrid, KpiCard, Toolbar } from '../ui/Page';
import { UnderlineTabPanel, UnderlineTabStrip, UnderlineTabs } from '../ui/Tabs';
import { MONEY_TABS, useUrlTab } from '../ui/pageTabs';
import { SearchInput } from '../ui/SearchInput';
import { DataTable, dash, type DataTableColumn } from '@/components/ui/data-table';
import { useServerTable } from '../ui/useServerTable';
import { BooksPanel } from '../money/BooksPanel';
import { NewInvoiceSheet } from '../money/NewInvoiceSheet';
import { Button } from '../ui/Button';
import { IconPlus } from '../icons';
import { StatusBadge } from '../ui/Badge';
import { EmptyState } from '../ui/EmptyState';
import {
  ErrorState,
  FiguresNotice,
  StaleNotice,
  UnresolvedState,
  refreshFailed,
  shownState,
} from '../ui/QueryStates';
import { PageSkeleton } from '../ui/Skeleton';
import { INVOICE_STATUS, display } from '../status';
import {
  money,
  moneyShort,
  daysOverdue,
  invoiceBalanceCents,
  balanceLabel,
} from '../money';
import { IconMoney } from '../icons';
import { WithOrgClock } from '../ui/WithOrgClock';

type Invoice = {
  id: string;
  invoiceNumber: number | null;
  customerId: string;
  /** Joined by the server — see the note on listJobs. */
  customerName: string | null;
  status: string;
  dueDate: string | null;
  totalCents: number;
  lateFeeCents: number;
  amountPaidCents: number;
  createdAt: string | Date;
};
type Payment = {
  id: string;
  invoiceId: string | null;
  method: string;
  status: string;
  amountCents: number;
  tipCents: number;
  createdAt: string | Date;
};

// Keys typed to the route's own list (pageTabs) — a label here cannot drift from what
// `/money`'s validateSearch admits.
const TABS: { key: (typeof MONEY_TABS)[number]; label: string }[] = [
  { key: 'invoices', label: 'Invoices' },
  { key: 'payments', label: 'Payments' },
  { key: 'books', label: 'Books' },
];

/** Money — one page, three tabs (PRD v2 §4). Invoices | Payments | Books. */
export function MoneyPage() {
  return (
    <WithOrgClock what="your money" icon={IconMoney} title="Money">
      {(tz) => <MoneyBoard tz={tz} />}
    </WithOrgClock>
  );
}

// Needs the shop's zone for one thing only — whether an invoice reads "Late" — but that one
// thing is the column an owner scans this page for.
function MoneyBoard({ tz }: { tz: string }) {
  const trpc = useTRPC();
  // Once per render, not once per invoice row.
  const today = todayISO(tz);
  const navigate = useNavigate();
  // In the URL, not useState (105): a bookkeeper's "the payments tab, this one" link has
  // to land on the payments tab.
  const [tab, setTab] = useUrlTab(MONEY_TABS);
  const [newInvoice, setNewInvoice] = useState(false);
  const { search, setSearch, query, table } = useServerTable();
  const invoices = useQuery({
    ...trpc.invoice.list.queryOptions(query),
    placeholderData: (prev) => prev,
  });
  const payments = useQuery(trpc.payment.list.queryOptions());
  // The header numbers are aggregates over EVERY invoice, so they cannot be reduced from
  // the page on screen — see services/invoice.ts invoiceSummary.
  const summary = useQuery(trpc.invoice.summary.queryOptions());

  if (invoices.isPending) return <PageSkeleton />;
  if (invoices.isError) {
    return (
      <PageRoot>
        <PageHeader title="Money" />
        <ErrorState
          icon={IconMoney}
          what="your invoices"
          onRetry={() => void invoices.refetch()}
        />
      </PageRoot>
    );
  }

  const rows = invoices.data.rows as Invoice[];
  /**
   * 🔴 The page gate above covers `invoices` only. `payments` had NO state branch at all,
   * so a failed or still-loading payment list rendered "No payments yet" — the one claim on
   * this tab a shop would act on — and reduced to a "Tips collected" figure of $0.
   */
  const paymentsState = shownState(payments, payments.data);
  const pays = (payments.data ?? []) as Payment[];
  const tipsCents = pays.reduce((s, p) => s + p.tipCents, 0);

  const invoiceColumns: DataTableColumn<Invoice>[] = [
    {
      // Keyed to INVOICE_SORT — its old key `number` sorted into an allow-list that
      // silently dropped it.
      key: 'invoice',
      label: 'Invoice',
      value: (i) => i.invoiceNumber ?? 0,
      cell: (i) => (
        <div>
          <p className="font-medium">{dash(i.customerName)}</p>
          <p className="font-mono text-xs text-muted-foreground">
            {i.invoiceNumber ? `#${i.invoiceNumber}` : 'Draft'}
          </p>
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      value: (i) => display(INVOICE_STATUS, i.status).label,
      cell: (i) => {
        const late = daysOverdue(i.dueDate, today);
        const isLate = (late ?? -1) > 0 && i.status !== 'paid' && i.status !== 'void';
        const s = display(INVOICE_STATUS, i.status);
        return (
          <StatusBadge tone={isLate ? 'danger' : s.tone}>
            {isLate ? `${late}d late` : s.label}
          </StatusBadge>
        );
      },
    },
    {
      key: 'due',
      label: 'Due',
      value: (i) => i.dueDate,
      cell: (i) => dash(i.dueDate ? dateOnly(i.dueDate) : null),
    },
    {
      key: 'total',
      label: 'Total',
      value: (i) => i.totalCents,
      searchValue: (i) => money(i.totalCents),
      cell: (i) => money(i.totalCents),
      align: 'right',
    },
    {
      key: 'balance',
      label: 'Balance',
      value: (i) => invoiceBalanceCents(i),
      searchValue: (i) => balanceLabel(invoiceBalanceCents(i)),
      cell: (i) => (
        <span className="font-medium">{balanceLabel(invoiceBalanceCents(i))}</span>
      ),
      align: 'right',
    },
  ];

  const paymentColumns: DataTableColumn<Payment>[] = [
    {
      key: 'when',
      label: 'Received',
      value: (p) => new Date(p.createdAt).getTime(),
      searchValue: (p) => new Date(p.createdAt).toLocaleDateString(),
      cell: (p) => new Date(p.createdAt).toLocaleDateString(),
    },
    {
      key: 'method',
      label: 'Method',
      value: (p) => p.method,
      cell: (p) => dash(p.method),
    },
    {
      key: 'status',
      label: 'Status',
      value: (p) => p.status,
      cell: (p) => dash(p.status),
    },
    {
      key: 'amount',
      label: 'Amount',
      value: (p) => p.amountCents,
      searchValue: (p) => money(p.amountCents),
      cell: (p) => money(p.amountCents),
      align: 'right',
    },
    {
      // Its own column, never added into Amount: what the invoice collected and what the
      // customer added on top are two different numbers to everyone who reads this table.
      key: 'tip',
      label: 'Tip',
      value: (p) => p.tipCents,
      searchValue: (p) => (p.tipCents > 0 ? money(p.tipCents) : ''),
      cell: (p) => (p.tipCents > 0 ? money(p.tipCents) : dash(null)),
      align: 'right',
    },
  ];

  return (
    <PageRoot>
      <PageHeader
        title="Money"
        right={
          tab === 'invoices' ? (
            <Button onClick={() => setNewInvoice(true)}>
              <IconPlus size={15} />
              New invoice
            </Button>
          ) : null
        }
      />
      {/* 🔴 One of N parallel paths, again: Quotes and both inventory aggregates got this
          and the MONEY page — the four tiles a shop reads to know what it is owed — did not.
          (Codex adversary, round 3.) */}
      <FiguresNotice
        show={summary.isError && summary.data === undefined}
        what="what you're owed"
        onRetry={() => void summary.refetch()}
      />
      <KpiGrid>
        {/* `—` until the aggregate lands. A 0 here would read as "you are owed nothing".
            The two money tiles say what they are measured over: every invoice ever, with the
            tax and the late fees in it. A report's Invoiced for a period is before tax and
            leaves late fees out, and without the line the two read as the same number
            disagreeing (ops insights 09). */}
        <KpiCard
          label="Outstanding"
          value={summary.data ? moneyShort(summary.data.outstandingCents) : '—'}
          tone="brand"
          basis="All time · tax and late fees included"
        />
        <KpiCard
          label="Overdue"
          value={summary.data?.overdueCount ?? '—'}
          tone={summary.data && summary.data.overdueCount > 0 ? 'danger' : undefined}
        />
        <KpiCard
          label="Collected"
          value={summary.data ? moneyShort(summary.data.collectedCents) : '—'}
          tone="success"
          basis="All time · tax and late fees included"
        />
        {/* The fourth tile follows the tab, not the data. Tips are a payroll question and
            belong beside the payments; making the tile appear and vanish with whether
            anyone tipped would move three other numbers under the reader's eyes. */}
        {tab === 'payments' ? (
          <KpiCard
            label="Tips collected"
            // Reduced from the same ungated payments array as the list below it, so this
            // read a confident $0 whenever that query had not answered — on a tile whose
            // whole job is a payroll number.
            value={paymentsState === 'ready' ? moneyShort(tipsCents) : '—'}
          />
        ) : (
          <KpiCard label="Open invoices" value={summary.data?.openCount ?? '—'} />
        )}
      </KpiGrid>

      <UnderlineTabs value={tab} onValueChange={setTab}>
        <UnderlineTabStrip tabs={TABS} />

        {/* ONE search box, shared by invoices and payments (they share useServerTable
            too), so it sits between the strip and the panels rather than being
            duplicated into two of them. Real panels below — not ternaries — so every
            trigger's aria-controls points at a panel that exists (review 105 m3);
            no keepMounted, because none of this holds a draft and unmount-on-switch
            is exactly what the ternaries did. */}
        {tab !== 'books' ? (
          <Toolbar>
            <SearchInput
              placeholder={tab === 'invoices' ? 'Search invoices…' : 'Search payments…'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label={tab === 'invoices' ? 'Search invoices' : 'Search payments'}
            />
          </Toolbar>
        ) : null}

        <UnderlineTabPanel value="invoices">
          <DataTable
            tableId="invoices"
            columns={invoiceColumns}
            rows={rows}
            rowKey={(i) => i.id}
            search={search}
            server={table(invoices.data.total, invoices.isFetching)}
            onRowClick={(i) =>
              void navigate({
                to: '/invoices/$invoiceId',
                params: { invoiceId: i.id },
              })
            }
            rowLabel={(i) =>
              `Open invoice ${i.invoiceNumber ? `#${i.invoiceNumber}` : 'draft'}`
            }
            empty={
              <EmptyState
                icon={IconMoney}
                title="No invoices yet"
                description="Invoice a completed job and the money shows up here — with late fees and reminders handled for you."
              />
            }
          />
        </UnderlineTabPanel>

        <UnderlineTabPanel value="payments" className="space-y-6">
          {/* The `empty` slot below only renders when there are NO rows, so with
              payments in hand a failed refresh had nowhere to say so. */}
          <StaleNotice
            show={refreshFailed(payments, payments.data)}
            onRetry={() => void payments.refetch()}
          />
          <DataTable
            tableId="payments"
            columns={paymentColumns}
            rows={pays}
            rowKey={(p) => p.id}
            search={search}
            empty={
              // The `empty` slot IS the seam: it renders exactly when there are no rows,
              // which is the moment "none" and "not known yet" are indistinguishable.
              paymentsState === 'ready' ? (
                <EmptyState
                  icon={IconMoney}
                  title="No payments yet"
                  description="Card and bank payments land here the moment a customer pays."
                />
              ) : (
                <UnresolvedState
                  state={paymentsState}
                  what="your payments"
                  icon={IconMoney}
                  error={payments.error}
                  onRetry={() => void payments.refetch()}
                  skeleton="h-16"
                />
              )
            }
          />
        </UnderlineTabPanel>

        <UnderlineTabPanel value="books">
          <BooksPanel />
        </UnderlineTabPanel>
      </UnderlineTabs>
      <NewInvoiceSheet open={newInvoice} onClose={() => setNewInvoice(false)} />
    </PageRoot>
  );
}
